import {ApiError, ApiErrorCode} from './ApiError';
import {reportError} from './Logger';

// fetch-based HTTP client that replaced axios. It keeps every behaviour the callers relied on:
// a base URL per client, a 30s timeout, JSON in and out, lowercase response headers,
// non-2xx statuses rejected, and every failure normalised into an ApiError (and logged once).

const TIMEOUT_MS = 30_000;

// Server-side requests keep the exact User-Agent axios sent, so WordPress and Cloudflare see
// the same requests as before. Browsers do not allow overriding it, so it is server-only.
export const SERVER_USER_AGENT = 'axios/1.4.0';

export interface HttpResponse<T> {
    data: T;
    headers: Record<string, string>;
}

export interface HttpClient {
    get: <T = unknown>(path: string, options?: {signal?: AbortSignal}) => Promise<HttpResponse<T>>;
    post: <T = unknown>(path: string, data?: unknown) => Promise<HttpResponse<T>>;
}

// Defensive trim — guards against accidental leading/trailing spaces in .env values.
function normalizeBaseUrl(raw: string | undefined): string {
    let result = (raw ?? '').trim();
    while (result.endsWith('/')) result = result.slice(0, -1);
    return result;
}

// On the server (SSR/getServerSideProps) call WordPress directly — no CORS restriction.
// In the browser proxy through /api/wp so the browser never issues a cross-origin request.
// Strip a trailing `/wp` namespace segment so the base resolves to `/wp-json`.
// Endpoints then carry their own namespace (`wp/v2/...` or `debut/v1/...`),
// letting custom namespaces sit alongside core ones instead of nested under `/wp`.
function normalizeBlogBaseUrl(raw: string | undefined): string {
    return normalizeBaseUrl(raw).replace(/\/wp$/, '');
}

function getBlogBaseUrl(): string {
    if (globalThis.window === undefined) {
        return normalizeBlogBaseUrl(process.env.NEXT_PUBLIC_BASE_URL_BLOG);
    }
    return '/api/wp';
}

// Same URL joining as axios: absolute URLs are used as-is, otherwise base + '/' + path.
const ABSOLUTE_URL = /^([a-z][a-z\d+\-.]*:)?\/\//i;
export function buildUrl(baseURL: string, path: string): string {
    if (!baseURL || ABSOLUTE_URL.test(path)) return path;
    return path ? `${baseURL.replace(/\/?\/$/, '')}/${path.replace(/^\/+/, '')}` : baseURL;
}

// axios parsed JSON responses and silently fell back to the raw text when a body wasn't JSON.
export function parseBody(text: string): unknown {
    if (!text) return text;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

const withSuffix = (url: string | undefined) => (url ? `: ${url}` : '');

/**
 * Converts a failed request into a typed ApiError, preserving the same codes and messages the
 * axios interceptor produced. reportError() runs here so every failure is logged in one place;
 * intentional cancellations are not reported.
 */
export function toApiError(
    error: unknown,
    request: {url?: string; method?: string; timedOut?: boolean; cancelled?: boolean; response?: {status: number; data: unknown}}
): ApiError {
    const {url, method} = request;

    if (error instanceof ApiError) {
        reportError(error);
        return error;
    }

    if (request.cancelled) {
        return new ApiError('Request cancelled', {code: 'CANCELLED', isCancelled: true});
    }

    if (request.timedOut) {
        const apiErr = new ApiError(`Request timed out${withSuffix(url)}`, {code: 'TIMEOUT', isTimeout: true, url, method});
        reportError(apiErr);
        return apiErr;
    }

    if (request.response) {
        const {status, data} = request.response;
        const responseData = data as Record<string, unknown> | undefined;
        const fromBody = typeof responseData === 'object' && responseData !== null ? responseData : undefined;
        const message = (fromBody?.message as string) ?? (fromBody?.error as string) ?? `HTTP ${status}`;
        const code: ApiErrorCode = status >= 500 ? 'SERVER_ERROR' : 'CLIENT_ERROR';
        // responseData is kept on the error so callers (e.g. HubSpot form error parsing) can read the raw body.
        const apiErr = new ApiError(message, {code, status, url, method, responseData: data});
        reportError(apiErr);
        return apiErr;
    }

    // fetch rejects with a TypeError when the request never got a response (DNS, CORS, offline).
    if (error instanceof TypeError) {
        const apiErr = new ApiError(`Network error — check CORS / connectivity${withSuffix(url)}`, {
            code: 'NETWORK_ERROR',
            isNetwork: true,
            url,
            method
        });
        reportError(apiErr);
        return apiErr;
    }

    const apiErr = new ApiError(error instanceof Error ? error.message : 'Unexpected error', {code: 'UNKNOWN'});
    reportError(apiErr);
    return apiErr;
}

/** The request body as axios sent it: none when undefined, strings as-is, anything else (incl. null) as JSON. */
const serializeBody = (body: unknown): string | undefined => {
    if (body === undefined) return undefined;
    return typeof body === 'string' ? body : JSON.stringify(body);
};

async function request<T>(
    baseURL: string,
    method: 'GET' | 'POST',
    path: string,
    body: unknown,
    signal?: AbortSignal
): Promise<HttpResponse<T>> {
    const controller = new AbortController();
    let timedOut = false;
    let cancelled = false;

    const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, TIMEOUT_MS);

    const onCallerAbort = () => {
        cancelled = true;
        controller.abort();
    };
    if (signal?.aborted) onCallerAbort();
    else signal?.addEventListener('abort', onCallerAbort);

    const headers: Record<string, string> = {Accept: 'application/json'};
    // Like axios: a Content-Type only when there is a body to describe.
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (globalThis.window === undefined) headers['User-Agent'] = SERVER_USER_AGENT;

    const context = {url: path, method};

    try {
        const response = await fetch(buildUrl(baseURL, path), {
            method,
            headers,
            body: serializeBody(body),
            signal: controller.signal
        });
        const data = parseBody(await response.text());
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
            responseHeaders[key] = value;
        });

        if (!response.ok) {
            throw toApiError(undefined, {...context, response: {status: response.status, data}});
        }
        return {data: data as T, headers: responseHeaders};
    } catch (error: unknown) {
        if (error instanceof ApiError) throw error;
        throw toApiError(error, {...context, timedOut, cancelled});
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onCallerAbort);
    }
}

function createClient(baseURL: string): HttpClient {
    return {
        get: <T>(path: string, options?: {signal?: AbortSignal}) => request<T>(baseURL, 'GET', path, undefined, options?.signal),
        // `?? null` as before: a POST always declares a JSON body, even when it is empty.
        post: <T>(path: string, data?: unknown) => request<T>(baseURL, 'POST', path, data ?? null)
    };
}

// Three named clients, one per backend.
export const mainApiClient = createClient(normalizeBaseUrl(process.env.NEXT_PUBLIC_BASE_URL));
export const blogApiClient = createClient(getBlogBaseUrl());
export const hubspotApiClient = createClient(normalizeBaseUrl(process.env.NEXT_PUBLIC_HUBSPOT_FORM_URL));

export default mainApiClient;
